<template>
  <div class="v2026-cal">
    <div class="v2026-cal__head">
      <slot name="header" :title="title" :title-parts="titleParts" :prev="onPrev" :next="onNext">
        <button type="button" class="v2026-cal__nav v2026-cal__nav--prev"
                @click="onPrev" aria-label="เดือนก่อนหน้า">
          <span class="v2026-cal__nav-ico" aria-hidden="true" :style="maskStyle(icons.prev)"></span>
        </button>
        <span class="v2026-cal__title">{{ title }}</span>
        <button type="button" class="v2026-cal__nav v2026-cal__nav--next"
                @click="onNext" aria-label="เดือนถัดไป">
          <span class="v2026-cal__nav-ico" aria-hidden="true" :style="maskStyle(icons.next)"></span>
        </button>
      </slot>
    </div>

    <div class="v2026-cal__weekdays">
      <template v-for="(label, index) in weekdayLabels">
        <slot name="weekday" :label="label" :index="index">
          <span :key="'wd-' + index" class="v2026-cal__weekday">{{ label }}</span>
        </slot>
      </template>
    </div>

    <div class="v2026-cal__grid">
      <template v-for="cell in cells">
        <span v-if="cell.blank" :key="cell.key"
              class="v2026-cal__cell v2026-cal__cell--empty" aria-hidden="true"></span>
        <slot v-else name="day" :cell="cell">
          <button :key="cell.key" type="button"
                  class="v2026-cal__cell"
                  :class="{ 'is-today': cell.isToday, 'is-selected': cell.isSelected }"
                  :aria-current="cell.isToday ? 'date' : null"
                  :aria-pressed="cell.isSelected ? 'true' : null"
                  @click="onSelect(cell)">
            <span class="v2026-cal__num">{{ cell.day }}</span>
            <!-- Always rendered on a non-blank cell, even at count 0, so the number never shifts. -->
            <span class="v2026-cal__dots" aria-hidden="true">
              <span v-for="(dot, i) in dotList(cell.count)" :key="i"
                    class="v2026-cal__dot" :class="{ 'v2026-cal__dot--more': dot.more }"></span>
            </span>
          </button>
        </slot>
      </template>
    </div>

    <div v-if="showCreateButton || $slots.footer || $scopedSlots.footer" class="v2026-cal__footer">
      <slot name="footer" :create="onCreate">
        <button v-if="showCreateButton" type="button" class="v2026-cal__create" @click="onCreate">
          <span class="v2026-cal__create-ico" aria-hidden="true"
                :style="maskStyle(icons.create)"></span>
          {{ createLabel }}
        </button>
      </slot>
    </div>
  </div>
</template>

<script>
// STYLE layer only — the logic lives in ../../composables/useMonthCalendar.
import { computed } from 'vue'
import { useMonthCalendar, THAI_MONTHS, DEFAULT_WEEKDAYS } from '../../composables/useMonthCalendar'

export default {
  name: 'month-calendar-v2026',
  props: {
    month: { type: [Object, Date, String], default: null }, // moment / Date / 'YYYY-MM'
    bookingsByDay: { type: Object, default: () => ({}) }, // { 'YYYY-MM-DD': number | any[] }
    selectedDate: { type: String, default: null }, // 'YYYY-MM-DD'
    today: { type: String, default: null }, // 'YYYY-MM-DD' (defaults to today in the core)
    weekdays: { type: Array, default: () => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] },
    weekStartsOn: { type: Number, default: 0 },
    yearMode: { type: String, default: 'gregorian' }, // 'gregorian' | 'buddhist'
    showCreateButton: { type: Boolean, default: true },
    createLabel: { type: String, default: 'สร้างรายการจอง' },
    maxDots: { type: Number, default: 3 } // 0 = uncapped (style-layer knob)
  },
  emits: ['month-change', 'select', 'create'],
  setup (props, { emit }) {
    const { cells, titleParts, weekdays: weekdayLabels, next, prev, select } = useMonthCalendar({
      month: () => props.month,
      bookingsByDay: () => props.bookingsByDay,
      selectedDate: () => props.selectedDate,
      today: () => props.today,
      weekStartsOn: () => props.weekStartsOn,
      weekdays: () => (props.weekdays && props.weekdays.length ? props.weekdays : DEFAULT_WEEKDAYS)
    })

    // Formatting is a style-layer choice; the core only exposes titleParts. Default = CE year.
    const title = computed(() => {
      const p = titleParts.value
      const year = props.yearMode === 'buddhist' ? p.buddhistYear : p.gregorianYear
      return `${THAI_MONTHS[p.monthIndex]} ${year}`
    })

    // Fixed footprint: cap at maxDots (0 = uncapped); the last becomes "more" when over cap.
    function dotList (count) {
      if (!count) return []
      const capped = props.maxDots === 0 ? count : Math.min(count, props.maxDots)
      const list = []
      for (let i = 1; i <= capped; i++) {
        const isMore = props.maxDots !== 0 && count > props.maxDots && i === capped
        list.push({ more: isMore })
      }
      return list
    }

    function onPrev () { emit('month-change', prev()) }
    function onNext () { emit('month-change', next()) }
    function onSelect (cell) {
      if (cell.blank) return
      select(cell.dateStr)
      emit('select', cell.dateStr)
    }
    function onCreate () { emit('create') }

    function maskStyle (url) {
      return { '-webkit-mask-image': 'url(' + url + ')', 'mask-image': 'url(' + url + ')' }
    }

    const icons = {
      prev: require('@/assets/icons2026/chevron-left.svg'),
      next: require('@/assets/icons2026/chevron-right.svg'),
      create: require('@/assets/icons2026/create-booking.svg')
    }

    return {
      cells,
      titleParts,
      weekdayLabels,
      title,
      dotList,
      onPrev,
      onNext,
      onSelect,
      onCreate,
      maskStyle,
      icons
    }
  }
}
</script>

<style scoped>
.v2026-cal {
  --v2026-cal-bg:                   var(--v2026-surface, #ffffff);
  --v2026-cal-border:               var(--v2026-border, #e4e7ec);
  --v2026-cal-border-width:         1px;
  --v2026-cal-radius:               var(--v2026-r-card, 12px);
  --v2026-cal-pad:                  var(--v2026-sp-3, 12px);

  --v2026-cal-title-color:          var(--v2026-text, #101828);
  --v2026-cal-title-fs:             var(--v2026-fs-body, 14px);
  --v2026-cal-title-weight:         600;

  --v2026-cal-chevron-size:         28px;
  --v2026-cal-chevron-icon-size:    16px;
  --v2026-cal-chevron-border:       var(--v2026-border, #e4e7ec);
  --v2026-cal-chevron-border-width: 1px;
  --v2026-cal-chevron-icon:         var(--v2026-icon, #475467);
  --v2026-cal-chevron-bg:           transparent;
  --v2026-cal-chevron-hover-bg:     var(--v2026-surface-hover, #e9ecf1);
  --v2026-cal-chevron-radius:       50%;

  --v2026-cal-weekday-color:        var(--v2026-text-muted, #98a2b3);
  --v2026-cal-weekday-fs:           var(--v2026-fs-meta, 12px);

  --v2026-cal-cell-gap:             2px;
  --v2026-cal-cell-radius:          50%;
  --v2026-cal-num-color:            var(--v2026-text, #101828);
  --v2026-cal-num-fs:               var(--v2026-fs-meta, 12px);
  --v2026-cal-cell-hover-bg:        var(--v2026-surface-hover, #e9ecf1);
  --v2026-cal-focus-ring:           rgba(49, 193, 215, 0.30);
  --v2026-cal-out-color:            var(--v2026-text-muted, #98a2b3);

  --v2026-cal-today-bg:             var(--v2026-color-main-soft, #31C1D714);
  --v2026-cal-today-ring:           transparent;
  --v2026-cal-today-num-color:      var(--v2026-text, #101828);
  --v2026-cal-today-num-weight:     600;

  --v2026-cal-selected-ring:        var(--v2026-color-main, #31C1D7);
  --v2026-cal-selected-ring-width:  2px;
  --v2026-cal-selected-bg:          transparent;
  --v2026-cal-selected-num-color:   var(--v2026-color-main, #31C1D7);
  --v2026-cal-selected-num-weight:  700;

  --v2026-cal-dot:                  #4990FF;
  --v2026-cal-dot-more:             #1E5FD0;
  --v2026-cal-dot-size:             4px;
  --v2026-cal-dot-more-size:        6px;
  --v2026-cal-dot-gap:              2px;
  --v2026-cal-dot-row-h:            8px;
  --v2026-cal-dot-bottom:           4px;

  --v2026-cal-create-border:        var(--v2026-color-main, #31C1D7);
  --v2026-cal-create-bg:            rgba(49, 193, 215, 0.08);
  --v2026-cal-create-bg-hover:      rgba(49, 193, 215, 0.18);
  --v2026-cal-create-color:         var(--v2026-color-main, #31C1D7);

  background: var(--v2026-cal-bg);
  border: var(--v2026-cal-border-width) solid var(--v2026-cal-border);
  border-radius: var(--v2026-cal-radius);
  padding: var(--v2026-cal-pad);
}

.v2026-cal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--v2026-sp-2, 8px);
}
.v2026-cal__title {
  font-size: var(--v2026-cal-title-fs);
  font-weight: var(--v2026-cal-title-weight);
  color: var(--v2026-cal-title-color);
}
.v2026-cal__nav {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--v2026-cal-chevron-size);
  height: var(--v2026-cal-chevron-size);
  padding: 0;
  border: var(--v2026-cal-chevron-border-width) solid var(--v2026-cal-chevron-border);
  border-radius: var(--v2026-cal-chevron-radius);
  background: var(--v2026-cal-chevron-bg);
  cursor: pointer;
  transition: background 150ms ease;
}
.v2026-cal__nav:hover { background: var(--v2026-cal-chevron-hover-bg); }
.v2026-cal__nav:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--v2026-cal-focus-ring);
}
.v2026-cal__nav-ico {
  width: var(--v2026-cal-chevron-icon-size);
  height: var(--v2026-cal-chevron-icon-size);
  background-color: var(--v2026-cal-chevron-icon);
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
}

.v2026-cal__weekdays,
.v2026-cal__grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: var(--v2026-cal-cell-gap);
}
.v2026-cal__weekday {
  text-align: center;
  font-size: var(--v2026-cal-weekday-fs);
  color: var(--v2026-cal-weekday-color);
  padding: var(--v2026-sp-1, 4px) 0;
}

.v2026-cal__cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  border: 0;
  background: transparent;
  border-radius: var(--v2026-cal-cell-radius);
  cursor: pointer;
  transition: background 150ms ease;
}
.v2026-cal__cell--empty { cursor: default; }
.v2026-cal__cell:not(.v2026-cal__cell--empty):hover {
  background: var(--v2026-cal-cell-hover-bg);
}
.v2026-cal__cell:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--v2026-cal-focus-ring);
}
.v2026-cal__num {
  font-size: var(--v2026-cal-num-fs);
  color: var(--v2026-cal-num-color);
}

.v2026-cal__cell.is-today { background: var(--v2026-cal-today-bg); }
.v2026-cal__cell.is-today .v2026-cal__num {
  color: var(--v2026-cal-today-num-color);
  font-weight: var(--v2026-cal-today-num-weight);
}

.v2026-cal__cell.is-selected {
  box-shadow: inset 0 0 0 var(--v2026-cal-selected-ring-width) var(--v2026-cal-selected-ring);
  background: var(--v2026-cal-selected-bg);
}
.v2026-cal__cell.is-today.is-selected { background: var(--v2026-cal-today-bg); }
.v2026-cal__cell.is-selected .v2026-cal__num {
  color: var(--v2026-cal-selected-num-color);
  font-weight: var(--v2026-cal-selected-num-weight);
}

.v2026-cal__dots {
  position: absolute;
  bottom: var(--v2026-cal-dot-bottom);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: var(--v2026-cal-dot-gap);
  height: var(--v2026-cal-dot-row-h);
}
.v2026-cal__dot {
  width: var(--v2026-cal-dot-size);
  height: var(--v2026-cal-dot-size);
  border-radius: 50%;
  background: var(--v2026-cal-dot);
}
.v2026-cal__dot--more {
  width: var(--v2026-cal-dot-more-size);
  height: var(--v2026-cal-dot-more-size);
  background: var(--v2026-cal-dot-more);
}

.v2026-cal__footer { margin-top: var(--v2026-sp-3, 12px); }
.v2026-cal__create {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--v2026-sp-2, 8px);
  width: 100%;
  padding: var(--v2026-sp-3, 12px);
  border: 1px solid var(--v2026-cal-create-border);
  border-radius: 8px;
  background: var(--v2026-cal-create-bg);
  color: var(--v2026-cal-create-color);
  font-size: var(--v2026-fs-body, 14px);
  font-weight: 600;
  cursor: pointer;
}
.v2026-cal__create:hover { background: var(--v2026-cal-create-bg-hover); }
.v2026-cal__create-ico {
  width: 18px;
  height: 18px;
  background-color: currentColor;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
}
</style>
